/**
 * Catalog label — decides whether a shop's product list should be called
 * "Menu" (restaurants / eateries) or "Catalog" (retail: electrical, flower,
 * grocery, pharmacy, service marketplaces, etc.).
 *
 * Uses the same `business_category_ids` structure as `isFoodShop`, but is
 * intentionally stricter: only the `food` vertical (restaurants / cafes /
 * bakeries) is treated as menu-worthy. Meat butchers, grocery stores and
 * every other retail vertical get "Catalog" — the UX label users expect
 * when they're browsing physical goods rather than dishes.
 *
 * Keeps a single source of truth so future themes / pages can call
 * `getCatalogLabel(shop, verticals)` instead of hard-coding strings.
 */

// Only true dining establishments use "Menu".
const MENU_SLUGS = new Set(["food"]);

function resolveSlug(id, byId) {
    const v = byId[id];
    if (!v) return id;                                       // legacy slug stored directly
    // Subcategory of Food → still menu.
    if (v.parent_id && byId[v.parent_id]) return byId[v.parent_id].slug || v.parent_id;
    return v.slug || id;
}

/**
 * @param {{business_category_ids?: string[]} | null | undefined} shop
 * @param {Array<{id?: string, slug?: string, parent_id?: string}>} verticals
 * @returns {"Menu" | "Catalog"}
 */
export function getCatalogLabel(shop, verticals = []) {
    const ids = shop?.business_category_ids || [];
    if (ids.length === 0) return "Catalog";
    const byId = {};
    for (const v of verticals) byId[v.id || v.slug] = v;
    return ids.some((id) => MENU_SLUGS.has(resolveSlug(id, byId))) ? "Menu" : "Catalog";
}
